import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Gift, Heart, Send, Users, Diamond, CheckCircle, 
  Clock, Mail, Sparkles, ArrowRight, Star, Loader2, Wand2,
  CreditCard, Calendar, User
} from 'lucide-react';

const API_URL = window.location.origin;

// Ontraport Gift Payment form
const ONTRAPORT_GIFT_FORM_UID = 'p2c260712f39'; // Update with actual gift payment form ID
const ONTRAPORT_GIFT_FORM_PARAMS = 'borderColor=%23000000&borderSize=0px&embed=true&formHeight=600&formWidth=100%25&popPosition=mc';

// AI-suggested messages that users can customize
const SUGGESTED_MESSAGES = [
  {
    personal_message: "I've been part of this incredible community of women, and it has truly transformed how I show up for myself. I immediately thought of you because I know you'd thrive here. This is a space where you can exhale, be seen, and be supported by women who truly understand.",
    why_gifting: "Because you deserve a sanctuary where you don't have to carry it all alone. This community will pour into you the way you pour into everyone else."
  },
  {
    personal_message: "Sis, I wanted to share something special with you. This community has become my sacred space - a place where I can be real, be held, and be challenged to grow. I know you've been carrying a lot, and I want you to experience this sisterhood too.",
    why_gifting: "Because even the strongest women need a soft place to land. You give so much to others - this is a space where you can receive."
  },
  {
    personal_message: "I've found my tribe in this community, and I couldn't help but think of you. These are high-capacity women who understand the unique challenges we face. No judgment, just genuine support and wisdom.",
    why_gifting: "Because you're the kind of woman who lifts others up, and you deserve to be surrounded by women who will lift you too."
  }
];

const GiftMembershipPage = () => {
  const { user } = useAuth();
  const { grandCelebrate } = useCelebration();
  const navigate = useNavigate();
  const ontraportContainerRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [myGifts, setMyGifts] = useState(null);
  const [loadingGifts, setLoadingGifts] = useState(true);
  const [suggestedIndex, setSuggestedIndex] = useState(0);
  const [paymentFormLoading, setPaymentFormLoading] = useState(false);
  const [processingPaidGift, setProcessingPaidGift] = useState(false);
  const [pendingPaidGifts, setPendingPaidGifts] = useState([]);
  const [completingGiftId, setCompletingGiftId] = useState(null);
  
  // Gift type selection
  const [giftType, setGiftType] = useState('free_trial'); // 'free_trial' or 'paid_membership'
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  const [formData, setFormData] = useState({
    recipient_name: '',
    recipient_email: '',
    personal_message: SUGGESTED_MESSAGES[0].personal_message,
    why_gifting: SUGGESTED_MESSAGES[0].why_gifting
  });

  useEffect(() => {
    fetchMyGifts();
    fetchPendingPaidGifts();
  }, []);

  // Listen for Ontraport form submission success
  useEffect(() => {
    const handleMessage = async (event) => {
      // Check if this is an Ontraport success message
      if (event.data && (event.data.type === 'opf-form-submit' || event.data.opfSubmitted)) {
        // Get pending gift data from localStorage
        const pendingGiftStr = localStorage.getItem('pendingGift');
        if (pendingGiftStr && showPaymentForm) {
          try {
            setProcessingPaidGift(true);
            const pendingGift = JSON.parse(pendingGiftStr);
            
            // Call the paid gift API
            const response = await axios.post(`${API_URL}/api/gift/paid-membership`, {
              recipient_email: pendingGift.recipient_email,
              recipient_name: pendingGift.recipient_name,
              personal_message: pendingGift.personal_message,
              why_gifting: pendingGift.why_gifting,
              gifter_name: user?.full_name || pendingGift.gifter_name,
              gifter_email: user?.email || pendingGift.gifter_email
            });
            
            // Clear pending gift
            localStorage.removeItem('pendingGift');
            
            setSent(true);
            setShowPaymentForm(false);
            grandCelebrate("Your paid gift membership is on its way! 💝");
            toast.success(response.data.message);
            
            // Refresh gifts list
            fetchMyGifts();
            
            // Reset form
            const nextIndex = (suggestedIndex + 1) % SUGGESTED_MESSAGES.length;
            setSuggestedIndex(nextIndex);
            setFormData({
              recipient_name: '',
              recipient_email: '',
              personal_message: SUGGESTED_MESSAGES[nextIndex].personal_message,
              why_gifting: SUGGESTED_MESSAGES[nextIndex].why_gifting
            });
          } catch (error) {
            console.error('Error creating paid gift:', error);
            toast.error(error.response?.data?.detail || 'Failed to process gift. Please contact support.');
          } finally {
            setProcessingPaidGift(false);
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showPaymentForm, suggestedIndex, user]);

  const fetchMyGifts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/my-gifts`);
      setMyGifts(response.data);
    } catch (error) {
      console.error('Error fetching gifts:', error);
    } finally {
      setLoadingGifts(false);
    }
  };

  const fetchPendingPaidGifts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/my-pending-paid-gifts`);
      const pendingGifts = response.data.pending_gifts || [];
      
      // Auto-complete all pending gifts immediately (no manual step needed)
      if (pendingGifts.length > 0) {
        for (const gift of pendingGifts) {
          try {
            await completePendingGiftSilent(gift.reference_id);
          } catch (error) {
            console.error(`Error auto-completing gift ${gift.reference_id}:`, error);
          }
        }
      }
      
      // Refresh the list after auto-completing
      const updatedResponse = await axios.get(`${API_URL}/api/gift/my-pending-paid-gifts`);
      setPendingPaidGifts(updatedResponse.data.pending_gifts || []);
    } catch (error) {
      console.error('Error fetching pending paid gifts:', error);
    }
  };

  // Silent version of completePendingGift for auto-completion
  const completePendingGiftSilent = async (referenceId) => {
    try {
      const response = await axios.post(`${API_URL}/api/gift/complete-pending/${referenceId}`);
      
      toast.success(`Gift sent to ${response.data.message?.includes('for') ? response.data.message.split('for ')[1]?.split('!')[0] : 'your friend'}! 🎉`);
      grandCelebrate("Your paid gift membership is on its way! 💝");
      
      // Refresh gifts list
      fetchMyGifts();
      localStorage.removeItem('pendingGift');
    } catch (error) {
      // Only show error if it's not "already completed" or "already a member"
      if (!error.response?.data?.detail?.includes('already')) {
        console.error('Error auto-completing pending gift:', error);
      }
    }
  };

  const completePendingGift = async (referenceId) => {
    try {
      setCompletingGiftId(referenceId);
      const response = await axios.post(`${API_URL}/api/gift/complete-pending/${referenceId}`);
      
      toast.success(response.data.message);
      grandCelebrate("Your paid gift membership is on its way! 💝");
      
      // Refresh both lists
      fetchMyGifts();
      fetchPendingPaidGifts();
      localStorage.removeItem('pendingGift');
    } catch (error) {
      console.error('Error completing pending gift:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete gift. Please contact support.');
    } finally {
      setCompletingGiftId(null);
    }
  };

  const cycleSuggestion = () => {
    const nextIndex = (suggestedIndex + 1) % SUGGESTED_MESSAGES.length;
    setSuggestedIndex(nextIndex);
    setFormData({
      ...formData,
      personal_message: SUGGESTED_MESSAGES[nextIndex].personal_message,
      why_gifting: SUGGESTED_MESSAGES[nextIndex].why_gifting
    });
    toast.success('New suggested message loaded - feel free to customize!');
  };

  // Load Ontraport payment form for paid gifts with pre-filled recipient data
  const loadPaymentForm = (referenceId = null) => {
    if (ontraportContainerRef.current) {
      ontraportContainerRef.current.innerHTML = '';
      setPaymentFormLoading(true);
      
      // Get pending gift data from localStorage
      const pendingGiftStr = localStorage.getItem('pendingGift');
      const pendingGift = pendingGiftStr ? JSON.parse(pendingGiftStr) : {};
      
      // Build params with pre-filled recipient data for Ontraport
      // These will populate the form fields in Ontraport
      const refId = referenceId || pendingGift.reference_id || '';
      const recipientEmail = pendingGift.recipient_email || formData.recipient_email || '';
      const recipientName = pendingGift.recipient_name || formData.recipient_name || '';
      const [firstName, ...lastNameParts] = recipientName.split(' ');
      const lastName = lastNameParts.join(' ') || '';
      
      // URL encode the values for Ontraport params
      // These field names should match your Ontraport form field merge codes
      const prefillParams = [
        `firstname=${encodeURIComponent(firstName)}`,
        `lastname=${encodeURIComponent(lastName)}`,
        `email=${encodeURIComponent(recipientEmail)}`,
        `f1944=${encodeURIComponent(refId)}`, // Custom field for reference_id - UPDATE THIS
        `f1945=${encodeURIComponent(pendingGift.gifter_name || user?.full_name || '')}`, // Gifter name - UPDATE THIS
        `f1946=${encodeURIComponent(pendingGift.gifter_email || user?.email || '')}` // Gifter email - UPDATE THIS
      ].join('&');
      
      const fullParams = `${ONTRAPORT_GIFT_FORM_PARAMS}&${prefillParams}`;
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://app.ontraport.com/js/ontraport/opt_assets/drivers/opf.js';
      script.setAttribute('data-opf-uid', ONTRAPORT_GIFT_FORM_UID);
      script.setAttribute('data-opf-params', fullParams);
      
      script.onload = () => setTimeout(() => setPaymentFormLoading(false), 1500);
      script.onerror = () => {
        setPaymentFormLoading(false);
        toast.error('Failed to load payment form. Please try again.');
      };
      
      ontraportContainerRef.current.appendChild(script);
      setTimeout(() => setPaymentFormLoading(false), 8000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_name || !formData.recipient_email || !formData.personal_message || !formData.why_gifting) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // If paid membership, create pending gift first then show payment form
    if (giftType === 'paid_membership') {
      setLoading(true);
      try {
        // Create pending gift record in backend
        const response = await axios.post(`${API_URL}/api/gift/pending-paid-gift`, {
          recipient_email: formData.recipient_email,
          recipient_name: formData.recipient_name,
          personal_message: formData.personal_message,
          why_gifting: formData.why_gifting
        });
        
        // Store reference_id and gift data for the payment form
        const pendingGiftData = {
          ...formData,
          reference_id: response.data.reference_id,
          gift_type: 'paid_membership',
          gifter_id: user?.id,
          gifter_name: response.data.gifter_name,
          gifter_email: user?.email,
          created_at: new Date().toISOString()  // Track when pending gift was created
        };
        localStorage.setItem('pendingGift', JSON.stringify(pendingGiftData));
        
        toast.success('Gift details saved! Complete payment to send the gift.');
        setShowPaymentForm(true);
        setTimeout(() => loadPaymentForm(response.data.reference_id), 100);
        
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to prepare gift. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Free trial gift - send immediately
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/gift/membership`, {
        ...formData,
        gift_type: 'free_trial'
      });
      
      setSent(true);
      grandCelebrate("Your gift of sisterhood is on its way! 💝");
      toast.success(response.data.message);
      
      // Refresh gifts list
      fetchMyGifts();
      
      // Reset form with new suggestion
      const nextIndex = (suggestedIndex + 1) % SUGGESTED_MESSAGES.length;
      setSuggestedIndex(nextIndex);
      setFormData({
        recipient_name: '',
        recipient_email: '',
        personal_message: SUGGESTED_MESSAGES[nextIndex].personal_message,
        why_gifting: SUGGESTED_MESSAGES[nextIndex].why_gifting
      });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send gift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 mb-4">
            <Gift className="h-8 w-8 text-pink-600" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-2">
            Gift the Sisterhood
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Know someone who needs this community? Gift them a membership and earn 
            <span className="font-semibold text-purple-600"> 100 diamond points ($5 value)</span> when they join!
          </p>
        </div>

        {/* Note: Pending paid gifts are now auto-completed when this page loads */}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Gift Form */}
          <div className="lg:col-span-2">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                  Send a Gift Membership
                </CardTitle>
                <CardDescription>
                  Your friend will receive a personalized email with your message
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sent ? (
                  <div className="text-center py-8">
                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl text-forest mb-2">Gift Sent Successfully!</h3>
                    <p className="text-gray-600 mb-6">
                      Your gift invitation has been sent. You'll earn 100 diamonds when they accept!
                    </p>
                    <Button 
                      onClick={() => {
                        setSent(false);
                        setShowPaymentForm(false);
                      }}
                      className="btn-primary"
                    >
                      Send Another Gift
                    </Button>
                  </div>
                ) : showPaymentForm ? (
                  <div>
                    {/* Back button */}
                    <button
                      onClick={() => setShowPaymentForm(false)}
                      className="flex items-center gap-2 text-forest/70 hover:text-forest mb-4 text-sm"
                    >
                      <ArrowRight className="h-4 w-4 rotate-180" />
                      Back to edit details
                    </button>
                    
                    {/* Gift summary */}
                    <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 mb-4">
                      <h3 className="font-medium text-forest mb-2">Gift Summary</h3>
                      <p className="text-sm text-gray-600">
                        <strong>Recipient:</strong> {formData.recipient_name} ({formData.recipient_email})
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Gift Type:</strong> Paid Monthly Membership ($97/month)
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Your card will be charged monthly until you cancel.
                      </p>
                    </div>
                    
                    {/* Instructions for the payment form */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <span>📋</span> Payment Form Instructions
                      </h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• The recipient's information will be pre-filled for their new account</li>
                        <li>• Enter <strong>your credit card</strong> to pay for the gift membership</li>
                        <li>• The recipient must be 18 years or older</li>
                        <li>• After payment, we'll send them a welcome email with login instructions</li>
                      </ul>
                    </div>
                    
                    {/* Payment form */}
                    <div className="relative min-h-[400px]">
                      {(paymentFormLoading || processingPaidGift) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                          <Loader2 className="h-8 w-8 animate-spin text-pink-500 mb-4" />
                          <p className="text-forest font-medium">
                            {processingPaidGift ? 'Creating Gift Membership...' : 'Loading Payment...'}
                          </p>
                        </div>
                      )}
                      <div ref={ontraportContainerRef} />
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Gift Type Selection */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Choose Gift Type</Label>
                      <div className="grid grid-cols-1 gap-3">
                        <div 
                          className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            giftType === 'free_trial' 
                              ? 'border-pink-500 bg-pink-50' 
                              : 'border-gray-200 hover:border-pink-200'
                          }`}
                          onClick={() => setGiftType('free_trial')}
                          data-testid="free-trial-option"
                        >
                          <div className={`aspect-square h-4 w-4 mt-1 rounded-full border-2 flex items-center justify-center ${
                            giftType === 'free_trial' ? 'border-pink-500' : 'border-gray-300'
                          }`}>
                            {giftType === 'free_trial' && (
                              <div className="h-2.5 w-2.5 rounded-full bg-pink-500" />
                            )}
                          </div>
                          <div className="ml-3">
                            <span className="font-medium text-forest">
                              <Calendar className="h-4 w-4 inline mr-2 text-pink-500" />
                              Gift a Free Trial
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              They get 30 days FREE, then decide if they want to continue ($97/month)
                            </p>
                            <Badge className="mt-2 bg-green-100 text-green-700">No cost to you</Badge>
                          </div>
                        </div>
                        
                        <div 
                          className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            giftType === 'paid_membership' 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-gray-200 hover:border-purple-200'
                          }`}
                          onClick={() => setGiftType('paid_membership')}
                          data-testid="paid-membership-option"
                        >
                          <div className={`aspect-square h-4 w-4 mt-1 rounded-full border-2 flex items-center justify-center ${
                            giftType === 'paid_membership' ? 'border-purple-500' : 'border-gray-300'
                          }`}>
                            {giftType === 'paid_membership' && (
                              <div className="h-2.5 w-2.5 rounded-full bg-purple-500" />
                            )}
                          </div>
                          <div className="ml-3">
                            <span className="font-medium text-forest">
                              <CreditCard className="h-4 w-4 inline mr-2 text-purple-500" />
                              Gift a Paid Membership
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              You pay the monthly fee ($97/month) - perfect for daughters, sisters, or close friends
                            </p>
                            <Badge className="mt-2 bg-purple-100 text-purple-700">$97/month on your card</Badge>
                            <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                              <span className="font-medium">⚠️ Note:</span> Recipient must be 18 years or older for membership
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* AI Suggestion Banner */}
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2">
                        <Wand2 className="h-4 w-4 text-purple-600" />
                        <span className="text-sm text-purple-700">
                          <strong>AI-suggested message</strong> - customize it to make it yours!
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cycleSuggestion}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Try Another
                      </Button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="recipient_name">Their Name</Label>
                        <Input
                          id="recipient_name"
                          value={formData.recipient_name}
                          onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                          placeholder="Friend's name"
                          className="input-base mt-1"
                          data-testid="gift-recipient-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="recipient_email">Their Email</Label>
                        <Input
                          id="recipient_email"
                          type="email"
                          value={formData.recipient_email}
                          onChange={(e) => setFormData({...formData, recipient_email: e.target.value})}
                          placeholder="friend@email.com"
                          className="input-base mt-1"
                          data-testid="gift-recipient-email"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="personal_message">Your Personal Message</Label>
                        <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                          Editable
                        </Badge>
                      </div>
                      <Textarea
                        id="personal_message"
                        value={formData.personal_message}
                        onChange={(e) => setFormData({...formData, personal_message: e.target.value})}
                        placeholder="Write a heartfelt message to your friend..."
                        className="input-base mt-1 min-h-[120px]"
                        data-testid="gift-personal-message"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This will appear in their email - feel free to personalize!
                      </p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="why_gifting">Why You're Gifting This</Label>
                        <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                          Editable
                        </Badge>
                      </div>
                      <Textarea
                        id="why_gifting"
                        value={formData.why_gifting}
                        onChange={(e) => setFormData({...formData, why_gifting: e.target.value})}
                        placeholder="I'm gifting this because I think you would really benefit from..."
                        className="input-base mt-1 min-h-[80px]"
                        data-testid="gift-why-gifting"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Help them understand why you chose this gift for them
                      </p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                      disabled={loading}
                      data-testid="send-gift-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending Gift...
                        </>
                      ) : giftType === 'paid_membership' ? (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Continue to Payment
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Gift Invitation
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* What They Get */}
            <Card className="card-base bg-gradient-to-br from-sand/50 to-white">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  What They'll Receive
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>
                    {giftType === 'paid_membership' 
                      ? <strong>Fully-paid</strong>
                      : <strong>30 days FREE</strong>
                    } Inner Circle membership
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access to all sister circles & sacred spaces</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Weekly transmissions & reflection prompts</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Connect with high-capacity women</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>
                    {giftType === 'paid_membership'
                      ? 'No payment needed from recipient'
                      : 'No credit card required to start'
                    }
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Your Reward */}
            <Card className="card-base border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Diamond className="h-5 w-5 text-purple-600" />
                  Your Reward
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600 mb-1">100</p>
                  <p className="text-sm text-purple-700">Diamond Points</p>
                  <p className="text-xs text-gray-500 mt-2">= $5 toward swag, coaching, or programs</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* My Referrals Section */}
        {myGifts && (
          <div className="mt-8">
            <h2 className="font-heading text-xl text-forest mb-4">Your Gift Activity</h2>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-forest">{myGifts.pending_gifts?.length || 0}</p>
                  <p className="text-sm text-gray-500">Pending Invitations</p>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-green-600">{myGifts.successful_referrals?.length || 0}</p>
                  <p className="text-sm text-gray-500">Successful Referrals</p>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-purple-600">{myGifts.total_referral_points_earned || 0}</p>
                  <p className="text-sm text-gray-500">Points Earned from Referrals</p>
                </CardContent>
              </Card>
            </div>

            {/* Pending Gifts */}
            {myGifts.pending_gifts?.length > 0 && (
              <Card className="card-base mb-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Pending Invitations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myGifts.pending_gifts.filter(g => g.status === 'pending').map((gift) => (
                      <div key={gift.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium text-forest">{gift.recipient_name}</p>
                          <p className="text-sm text-gray-500">{gift.recipient_email}</p>
                        </div>
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Awaiting Response
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Successful Referrals */}
            {myGifts.successful_referrals?.length > 0 && (
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-green-500" />
                    Successful Referrals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myGifts.successful_referrals.map((referral) => (
                      <div key={referral.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium text-forest">{referral.referred_name}</p>
                          <p className="text-sm text-gray-500">
                            Joined {new Date(referral.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">
                          +{referral.points_awarded} points
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GiftMembershipPage;
